import {
	AdministrativeAuthorizationType,
	ArchitecteMission,
	BuilderMainActivity,
	BureauEtudeMission,
	ConstructionSiteDocumentType,
	ConstructionSiteSubmissionPayload,
	ControleurTechniqueMission,
	DesignerType,
	GeotechnicienMission,
	MaitreOeuvreMission,
	UpdateReceptionParticipantActivitiesAndFeesDto,
	UpdateReceptionParticipantFeesDto,
} from "@dune/types";
import { Proposition, ReceptionStep1Payload } from "@dune/types";
import { getErrorMessage } from "@dune/vars";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { privateAxios } from "../axios";
import { constructionSiteAnalysisQueryKeys } from "../query-keys/constructionSiteAnalysis.query-keys";
import { constructionSiteSubmissionQueryKeys } from "../query-keys/constructionSiteSubmission.query-keys";
import { constructionSiteReceptionQueryKeys } from "../query-keys/constructionSiteReception.query-keys";
import { constructionSitesQueryKeys } from "../query-keys/constructionSites.query-keys";

interface ConstructionSiteSubmissionErrorData {
	code?: string;
	message?: string;
	totalDeclaredAmount?: number;
	constructionCost?: number;
	requiresPricingCheck?: boolean;
	suggestionMessage?: string;
	currentStatus?: string;
}

export const createOrGetConstructionSiteSubmission = async (
	constructionSiteId: number,
): Promise<ConstructionSiteSubmissionPayload> => {
	const { data } = await privateAxios.post<ConstructionSiteSubmissionPayload>(
		"/construction-sites-submission",
		{ constructionSiteId },
	);
	return data;
};

export const useCreateOrGetConstructionSiteSubmission = () => {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: createOrGetConstructionSiteSubmission,
		onSuccess: (data) => {
			queryClient.setQueryData(
				constructionSiteSubmissionQueryKeys.detail(data.constructionSiteId),
				data,
			);
		},
		onError: (error) => {
			const errorMessage = getErrorMessage(error);
			toast.error("Erreur lors de la récupération de la soumission", {
				description: errorMessage,
			});
		},
	});
};

export const updateConstructionSiteSubmissionBinderWarrantiesSelected = async (
	constructionSiteId: number,
	binderWarrantyIds: number[],
) => {
	const { data } = await privateAxios.patch(
		`/construction-sites-submission/${constructionSiteId}/binder-warranties-selected`,
		{
			binderWarrantyIds,
		},
	);
	return data;
};

export const useUpdateConstructionSiteSubmissionBinderWarrantiesSelected = (
	constructionSiteId: number,
) => {
	return useMutation({
		mutationFn: (binderWarrantyIds: number[]) =>
			updateConstructionSiteSubmissionBinderWarrantiesSelected(
				constructionSiteId,
				binderWarrantyIds,
			),
	});
};

export const submitConstructionSiteSubmission = async (
	constructionSiteId: number,
	options?: { bypassLowAmountCheck?: boolean; reservesDescription?: string[] },
) => {
	const payload: {
		bypassLowAmountCheck?: boolean;
		reservesDescription?: string[];
	} = {};
	if (options?.bypassLowAmountCheck) payload.bypassLowAmountCheck = true;
	if (Array.isArray(options?.reservesDescription))
		payload.reservesDescription = options.reservesDescription;
	const { data } = await privateAxios.patch<Proposition[]>(
		`/construction-sites-submission/${constructionSiteId}/submit`,
		payload,
	);
	return data;
};

export const useSubmitConstructionSiteSubmission = () => {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: ({
			constructionSiteId,
			options,
		}: {
			constructionSiteId: number;
			options?: {
				bypassLowAmountCheck?: boolean;
				reservesDescription?: string[];
			};
		}) => submitConstructionSiteSubmission(constructionSiteId, options),
		onSuccess: async (_data, { constructionSiteId }) => {
			toast.success("Soumission du chantier validée avec succès");

			queryClient.invalidateQueries({
				queryKey:
					constructionSiteSubmissionQueryKeys.detail(constructionSiteId),
			});
		},
		onError: (error) => {
			const errorData = (
				error as {
					response?: {
						data?: ConstructionSiteSubmissionErrorData;
					};
				}
			)?.response?.data;
			if (
				errorData?.code === "CONSTRUCTION_COST_VALIDATION_REQUIRED" ||
				errorData?.code === "CONSTRUCTION_COST_EXCEEDED" ||
				errorData?.code === "DECLARED_AMOUNT_TOO_LOW"
			) {
				return;
			}

			if (errorData?.code === "CONSTRUCTION_COST_NOT_DEFINED") {
				toast.error("Coût de construction manquant", {
					description:
						errorData?.message ||
						"Le coût de construction doit être défini avant la soumission",
				});
				return;
			}

			if (errorData?.code === "ALREADY_SUBMITTED") {
				toast.error("Chantier déjà soumis", {
					description: errorData?.message || "Ce chantier a déjà été soumis",
				});
				return;
			}

			if (errorData?.code === "INVALID_STATUS_FOR_SUBMISSION") {
				toast.error("Statut invalide", {
					description:
						errorData?.message ||
						"Ce chantier ne peut pas être soumis dans son état actuel",
				});
				return;
			}
			const errorMessage = getErrorMessage(error);
			toast.error("Erreur lors de la validation de la soumission", {
				description: errorMessage,
			});
		},
	});
};

export const validateReceptionAnalysis = async (
	constructionSiteId: number,
	data: {
		addReservesEnabled: boolean;
		selectedWarrantiesData: Record<number, { createReceipt: boolean }>;
	},
) => {
	await privateAxios.patch(
		`/construction-sites-reception/${constructionSiteId}/analysis/validate`,
		data,
	);
};

export const useValidateReceptionAnalysis = () => {
	return useMutation({
		mutationFn: ({
			constructionSiteId,
			data,
		}: {
			constructionSiteId: number;
			data: {
				addReservesEnabled: boolean;
				selectedWarrantiesData: Record<number, { createReceipt: boolean }>;
			};
		}) => validateReceptionAnalysis(constructionSiteId, data),
	});
};

export const validateReception = async (
	constructionSiteId: number,
	data: { receptionDate?: string; hasAuthorizationChange?: boolean },
) => {
	await privateAxios.patch(
		`/construction-sites-reception/${constructionSiteId}/validate`,
		data,
	);
};

export const useValidateReception = () => {
	return useMutation({
		mutationFn: ({
			constructionSiteId,
			data,
		}: {
			constructionSiteId: number;
			data: { receptionDate?: string; hasAuthorizationChange?: boolean };
		}) => validateReception(constructionSiteId, data),
	});
};

type UpdateConstructionSiteSubmissionOperationParams = {
	administrativeAuthorizationType?: AdministrativeAuthorizationType | null;
	authorizationReference?: string;
	coOwnersSyndicDenomination?: string;
	operationDescription?: string;
};

export const updateConstructionSiteSubmissionOperation = async (
	constructionSiteId: number,
	params: UpdateConstructionSiteSubmissionOperationParams,
) => {
	const { data } = await privateAxios.patch(
		`/construction-sites-submission/${constructionSiteId}/operation`,
		params,
	);
	return data;
};

export const useUpdateConstructionSiteSubmissionOperation = (
	constructionSiteId: number,
) => {
	return useMutation({
		mutationFn: (params: UpdateConstructionSiteSubmissionOperationParams) =>
			updateConstructionSiteSubmissionOperation(constructionSiteId, params),
	});
};

export const updateConstructionSiteReception = async (
	constructionSiteId: number,
	data: Partial<
		Pick<ReceptionStep1Payload, "receptionDate" | "hasAuthorizationChange">
	>,
) => {
	const { receptionDate, hasAuthorizationChange } = data;
	const payload: { receptionDate?: string; hasAuthorizationChange?: boolean } =
		{};
	if (typeof hasAuthorizationChange === "boolean")
		payload.hasAuthorizationChange = hasAuthorizationChange;
	if (typeof receptionDate === "string") payload.receptionDate = receptionDate;
	const res = await privateAxios.patch(
		`/construction-sites-reception/${constructionSiteId}`,
		payload,
	);
	return res.data;
};

export const useUpdateConstructionSiteReception = (
	constructionSiteId: number,
) => {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: (
			data: Partial<
				Pick<ReceptionStep1Payload, "receptionDate" | "hasAuthorizationChange">
			>,
		) => updateConstructionSiteReception(constructionSiteId, data),
		onSuccess: () => {
			queryClient.invalidateQueries({
				queryKey: [
					...constructionSiteSubmissionQueryKeys.detail(constructionSiteId),
					"reception",
				],
			});
		},
		onError: () => {
			toast.error(
				"Erreur lors de l'enregistrement des informations de réception",
			);
		},
	});
};

export const updateConstructionSiteValidation = async (
	constructionSiteId: number,
	data: {
		validated?: boolean | null;
		addReservesEnabled?: boolean | null;
		reserves?: string[];
		onlySave?: boolean;
	},
) => {
	const res = await privateAxios.patch(
		`/construction-sites-submission/${constructionSiteId}/validation`,
		data,
	);
	return res.data;
};

export const useUpdateConstructionSiteValidation = (
	constructionSiteId: number,
) => {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: (data: {
			validated?: boolean | null;
			addReservesEnabled?: boolean | null;
			reserves?: string[];
			onlySave?: boolean;
		}) => updateConstructionSiteValidation(constructionSiteId, data),
		onSuccess: () => {
			queryClient.invalidateQueries({
				queryKey: [
					...constructionSiteSubmissionQueryKeys.detail(constructionSiteId),
					"validation",
				],
			});
		},
	});
};

export interface AddDesignerParticipantDto {
	designerType: DesignerType;
	feeAgreementsFilesIds: string[];
	decennialCertificatesFilesIds?: string[];
	architecteMissions?: ArchitecteMission[];
	maitreOeuvreMissions?: MaitreOeuvreMission[];
	geotechnicienMissions?: GeotechnicienMission[];
	controleurTechniqueMissions?: ControleurTechniqueMission[];
	bureauEtudeMissions?: BureauEtudeMission[];
	siren?: string;
	denomination: string;
	registrationNumber?: string;
	country?: string;
	companyName?: string;
	streetNumber?: string;
	streetName?: string;
	postalCode?: string;
	city?: string;
	totalFeesTTC: number;
	totalFeesHT?: number | null;
}

export interface UpdateDesignerParticipantDto
	extends Partial<AddDesignerParticipantDto> {}

export interface UpdateBuilderParticipantDto
	extends Partial<AddBuilderParticipantDto> {}

export interface UpdateMaterialParticipantDto
	extends Partial<AddMaterialParticipantDto> {}

export interface AddBuilderParticipantDto {
	quotesFilesIds: string[];
	decennialCertificatesFilesIds?: string[];
	activities: BuilderMainActivity[];
	siren?: string;
	denomination: string;
	registrationNumber?: string;
	country?: string;
	companyName?: string;
	streetNumber?: string;
	streetName?: string;
	postalCode?: string;
	city?: string;
	totalFeesTTC: number;
	totalFeesHT?: number | null;
}

export interface AddMaterialParticipantDto {
	quoteFilesIds: string[];
	materialTypes: string[];
	totalFeesTTC: number;
	totalFeesHT?: number | null;
}

const addDesignerParticipant = async (
	constructionSiteId: number,
	data: AddDesignerParticipantDto,
): Promise<void> => {
	await privateAxios.post(
		`/construction-sites-submission/${constructionSiteId}/add-designer-participant`,
		data,
	);
};

const addBuilderParticipant = async (
	constructionSiteId: number,
	data: AddBuilderParticipantDto,
): Promise<void> => {
	await privateAxios.post(
		`/construction-sites-submission/${constructionSiteId}/add-builder-participant`,
		data,
	);
};

const addMaterialParticipant = async (
	constructionSiteId: number,
	data: AddMaterialParticipantDto,
): Promise<void> => {
	await privateAxios.post(
		`/construction-sites-submission/${constructionSiteId}/add-material-participant`,
		data,
	);
};

// Reception-specific add mutations
const addDesignerParticipantToReception = async (
	constructionSiteId: number,
	data: AddDesignerParticipantDto,
): Promise<void> => {
	await privateAxios.post(
		`/construction-sites-reception/${constructionSiteId}/add-designer-participant`,
		data,
	);
};

const addBuilderParticipantToReception = async (
	constructionSiteId: number,
	data: AddBuilderParticipantDto,
): Promise<void> => {
	await privateAxios.post(
		`/construction-sites-reception/${constructionSiteId}/add-builder-participant`,
		data,
	);
};

const addMaterialParticipantToReception = async (
	constructionSiteId: number,
	data: AddMaterialParticipantDto,
): Promise<void> => {
	await privateAxios.post(
		`/construction-sites-reception/${constructionSiteId}/add-material-participant`,
		data,
	);
};

export const useAddDesignerParticipant = (constructionSiteId: number) => {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: (data: AddDesignerParticipantDto) =>
			addDesignerParticipant(constructionSiteId, data),
		onSuccess: () => {
			queryClient.invalidateQueries({
				queryKey:
					constructionSiteSubmissionQueryKeys.participants(constructionSiteId),
			});
			toast.success("Concepteur ajouté avec succès");
		},
		onError: (error) => {
			const errorMessage = getErrorMessage(error);
			toast.error("Erreur lors de l'ajout du concepteur", {
				description: errorMessage,
			});
		},
	});
};

export const useAddBuilderParticipant = (constructionSiteId: number) => {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: (data: AddBuilderParticipantDto) =>
			addBuilderParticipant(constructionSiteId, data),
		onSuccess: () => {
			queryClient.invalidateQueries({
				queryKey:
					constructionSiteSubmissionQueryKeys.participants(constructionSiteId),
			});
			toast.success("Réalisateur ajouté avec succès");
		},
		onError: (error) => {
			const errorMessage = getErrorMessage(error);
			toast.error("Erreur lors de l'ajout du réalisateur", {
				description: errorMessage,
			});
		},
	});
};

export const useAddMaterialParticipant = (constructionSiteId: number) => {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: (data: AddMaterialParticipantDto) =>
			addMaterialParticipant(constructionSiteId, data),
		onSuccess: () => {
			queryClient.invalidateQueries({
				queryKey:
					constructionSiteSubmissionQueryKeys.participants(constructionSiteId),
			});
			toast.success("Matériau ajouté avec succès");
		},
		onError: (error) => {
			const errorMessage = getErrorMessage(error);
			toast.error("Erreur lors de l'ajout du matériau", {
				description: errorMessage,
			});
		},
	});
};

export const useAddDesignerParticipantToReception = (
	constructionSiteId: number,
) => {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: (data: AddDesignerParticipantDto) =>
			addDesignerParticipantToReception(constructionSiteId, data),
		onSuccess: () => {
			queryClient.invalidateQueries({
				queryKey:
					constructionSiteSubmissionQueryKeys.participantsReception(
						constructionSiteId,
					),
			});
			toast.success("Concepteur ajouté à la réception avec succès");
		},
		onError: (error) => {
			const errorMessage = getErrorMessage(error);
			toast.error("Erreur lors de l'ajout du concepteur à la réception", {
				description: errorMessage,
			});
		},
	});
};

export const useAddBuilderParticipantToReception = (
	constructionSiteId: number,
) => {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: (data: AddBuilderParticipantDto) =>
			addBuilderParticipantToReception(constructionSiteId, data),
		onSuccess: () => {
			queryClient.invalidateQueries({
				queryKey:
					constructionSiteSubmissionQueryKeys.participantsReception(
						constructionSiteId,
					),
			});
			toast.success("Réalisateur ajouté à la réception avec succès");
		},
		onError: (error) => {
			const errorMessage = getErrorMessage(error);
			toast.error("Erreur lors de l'ajout du réalisateur à la réception", {
				description: errorMessage,
			});
		},
	});
};

export const useAddMaterialParticipantToReception = (
	constructionSiteId: number,
) => {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: (data: AddMaterialParticipantDto) =>
			addMaterialParticipantToReception(constructionSiteId, data),
		onSuccess: () => {
			queryClient.invalidateQueries({
				queryKey:
					constructionSiteSubmissionQueryKeys.participantsReception(
						constructionSiteId,
					),
			});
			toast.success("Matériau ajouté à la réception avec succès");
		},
		onError: (error) => {
			const errorMessage = getErrorMessage(error);
			toast.error("Erreur lors de l'ajout du matériau à la réception", {
				description: errorMessage,
			});
		},
	});
};

export const deleteParticipantFromConstructionSiteSubmission = async (
	constructionSiteId: number,
	participantId: number,
) => {
	await privateAxios.delete(
		`/construction-sites-submission/${constructionSiteId}/participants/${participantId}`,
	);
};

export const useDeleteParticipantFromConstructionSiteSubmission = (
	constructionSiteId: number,
) => {
	return useMutation({
		mutationFn: (participantId: number) =>
			deleteParticipantFromConstructionSiteSubmission(
				constructionSiteId,
				participantId,
			),
	});
};

export const updateDesignerParticipant = async (
	constructionSiteId: number,
	participantId: number,
	data: UpdateDesignerParticipantDto,
): Promise<void> => {
	await privateAxios.patch(
		`/construction-sites-submission/${constructionSiteId}/update-designer-participant/${participantId}`,
		data,
	);
};

export const useUpdateDesignerParticipant = (
	constructionSiteId: number,
	participantId: number,
) => {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: (data: UpdateDesignerParticipantDto) =>
			updateDesignerParticipant(constructionSiteId, participantId, data),
		onSuccess: () => {
			queryClient.invalidateQueries({
				queryKey:
					constructionSiteSubmissionQueryKeys.participants(constructionSiteId),
			});
			toast.success("Concepteur modifié avec succès");
		},
		onError: (error) => {
			const errorMessage = getErrorMessage(error);
			toast.error("Erreur lors de la modification du concepteur", {
				description: errorMessage,
			});
		},
	});
};

export const updateBuilderParticipant = async (
	constructionSiteId: number,
	participantId: number,
	data: UpdateBuilderParticipantDto,
): Promise<void> => {
	await privateAxios.patch(
		`/construction-sites-submission/${constructionSiteId}/update-builder-participant/${participantId}`,
		data,
	);
};

export const useUpdateBuilderParticipant = (
	constructionSiteId: number,
	participantId: number,
) => {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: (data: UpdateBuilderParticipantDto) =>
			updateBuilderParticipant(constructionSiteId, participantId, data),
		onSuccess: () => {
			queryClient.invalidateQueries({
				queryKey:
					constructionSiteSubmissionQueryKeys.participants(constructionSiteId),
			});
			toast.success("Réalisateur modifié avec succès");
		},
		onError: (error) => {
			const errorMessage = getErrorMessage(error);
			toast.error("Erreur lors de la modification du réalisateur", {
				description: errorMessage,
			});
		},
	});
};

export const updateMaterialParticipant = async (
	constructionSiteId: number,
	participantId: number,
	data: UpdateMaterialParticipantDto,
): Promise<void> => {
	await privateAxios.patch(
		`/construction-sites-submission/${constructionSiteId}/update-material-participant/${participantId}`,
		data,
	);
};

export const useUpdateMaterialParticipant = (
	constructionSiteId: number,
	participantId: number,
) => {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: (data: UpdateMaterialParticipantDto) =>
			updateMaterialParticipant(constructionSiteId, participantId, data),
		onSuccess: () => {
			queryClient.invalidateQueries({
				queryKey:
					constructionSiteSubmissionQueryKeys.participants(constructionSiteId),
			});
			toast.success("Matériau modifié avec succès");
		},
		onError: (error) => {
			const errorMessage = getErrorMessage(error);
			toast.error("Erreur lors de la modification du matériau", {
				description: errorMessage,
			});
		},
	});
};

export const linkFileToConstructionSiteSubmission = async (
	constructionSiteId: number,
	params: {
		fileId: string;
		type: ConstructionSiteDocumentType;
		participantId?: number;
	},
) => {
	const { data } = await privateAxios.post(
		`/construction-sites-submission/${constructionSiteId}/link-file`,
		params,
	);
	return data;
};

export const useLinkFileToConstructionSiteSubmission = (
	constructionSiteId: number,
) => {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: (params: {
			fileId: string;
			type: ConstructionSiteDocumentType;
			participantId?: number;
		}) => linkFileToConstructionSiteSubmission(constructionSiteId, params),
		onSuccess: () => {
			queryClient.invalidateQueries({
				queryKey:
					constructionSiteSubmissionQueryKeys.detail(constructionSiteId),
			});
			queryClient.invalidateQueries({
				queryKey:
					constructionSiteSubmissionQueryKeys.operation(constructionSiteId),
			});
			queryClient.invalidateQueries({
				queryKey:
					constructionSiteSubmissionQueryKeys.participants(constructionSiteId),
			});
			queryClient.invalidateQueries({
				queryKey:
					constructionSiteSubmissionQueryKeys.referenceableFiles(
						constructionSiteId,
					),
			});
			queryClient.invalidateQueries({
				queryKey: [
					...constructionSiteAnalysisQueryKeys.analysisData(constructionSiteId),
					"supporting-documents-analysis",
				],
			});
		},
		onError: (error) => {
			const errorMessage = getErrorMessage(error);
			toast.error("Erreur lors de la liaison du fichier", {
				description: errorMessage,
			});
		},
	});
};

export const linkExternalDecennialCertificates = async (
	constructionSiteId: number,
) => {
	await privateAxios.post(
		`/construction-sites-submission/${constructionSiteId}/link-external-decennial-certificates`,
	);
};

export const useLinkExternalDecennialCertificates = (
	constructionSiteId: number,
) => {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: () => linkExternalDecennialCertificates(constructionSiteId),
		onSuccess: () => {
			queryClient.invalidateQueries({
				queryKey:
					constructionSiteSubmissionQueryKeys.participants(constructionSiteId),
			});
		},
	});
};

export const unlinkFileFromConstructionSiteSubmission = async (
	constructionSiteId: number,
	fileId: string,
) => {
	await privateAxios.delete(
		`/construction-sites-submission/${constructionSiteId}/unlink-file/${fileId}`,
	);
};

export const useUnlinkFileFromConstructionSiteSubmission = (
	constructionSiteId: number,
) => {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: (fileId: string) =>
			unlinkFileFromConstructionSiteSubmission(constructionSiteId, fileId),
		onSuccess: () => {
			queryClient.invalidateQueries({
				queryKey:
					constructionSiteSubmissionQueryKeys.detail(constructionSiteId),
			});
			queryClient.invalidateQueries({
				queryKey: constructionSitesQueryKeys.documents(constructionSiteId),
			});
			queryClient.invalidateQueries({
				queryKey:
					constructionSiteSubmissionQueryKeys.operation(constructionSiteId),
			});
			queryClient.invalidateQueries({
				queryKey:
					constructionSiteSubmissionQueryKeys.participants(constructionSiteId),
			});
			queryClient.invalidateQueries({
				queryKey:
					constructionSiteSubmissionQueryKeys.referenceableFiles(
						constructionSiteId,
					),
			});
			toast.success("Document déréférencé avec succès");
		},
		onError: () => {
			toast.error("Erreur lors de la suppression du document");
		},
	});
};

export const updateReceptionParticipantFees = async (
	constructionSiteId: number,
	participantId: number,
	data: UpdateReceptionParticipantFeesDto,
): Promise<void> => {
	await privateAxios.patch(
		`/construction-sites-reception/${constructionSiteId}/participants/${participantId}/fees`,
		data,
	);
};

export const useUpdateReceptionParticipantFees = (
	constructionSiteId: number,
	participantId: number,
) => {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: (data: UpdateReceptionParticipantFeesDto) =>
			updateReceptionParticipantFees(constructionSiteId, participantId, data),
		onSuccess: () => {
			queryClient.invalidateQueries({
				queryKey:
					constructionSiteSubmissionQueryKeys.participantsReception(
						constructionSiteId,
					),
			});
		},
	});
};

export const deleteReceptionParticipant = async (
	constructionSiteId: number,
	participantId: number,
) => {
	await privateAxios.delete(
		`/construction-sites-reception/${constructionSiteId}/participants/${participantId}`,
	);
};

export const useDeleteReceptionParticipant = (constructionSiteId: number) => {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: (participantId: number) =>
			deleteReceptionParticipant(constructionSiteId, participantId),
		onSuccess: () => {
			queryClient.invalidateQueries({
				queryKey:
					constructionSiteSubmissionQueryKeys.participantsReception(
						constructionSiteId,
					),
			});
		},
	});
};

export const updateReceptionParticipantActivities = async (
	constructionSiteId: number,
	participantId: number,
	data: { totalMissionsOrActivitiesOrMaterialTypes: string[] },
): Promise<void> => {
	await privateAxios.patch(
		`/construction-sites-reception/${constructionSiteId}/participants/${participantId}/activities`,
		data,
	);
};

export const updateReceptionParticipantActivitiesAndFees = async (
	constructionSiteId: number,
	participantId: number,
	data: UpdateReceptionParticipantActivitiesAndFeesDto,
): Promise<void> => {
	await privateAxios.patch(
		`/construction-sites-reception/${constructionSiteId}/participants/${participantId}/activities-and-fees`,
		data,
	);
};

export const useUpdateReceptionParticipantActivitiesAndFees = (
	constructionSiteId: number,
	participantId: number,
) => {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: (data: UpdateReceptionParticipantActivitiesAndFeesDto) =>
			updateReceptionParticipantActivitiesAndFees(
				constructionSiteId,
				participantId,
				data,
			),
		onSuccess: () => {
			queryClient.invalidateQueries({
				queryKey:
					constructionSiteSubmissionQueryKeys.participantsReception(
						constructionSiteId,
					),
			});
		},
		onError: (error) => {
			const errorMessage = getErrorMessage(error);
			toast.error("Erreur lors de la mise à jour", {
				description: errorMessage,
			});
		},
	});
};

export const useUpdateReceptionParticipantActivities = (
	constructionSiteId: number,
	participantId: number,
) => {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: (data: {
			totalMissionsOrActivitiesOrMaterialTypes: string[];
		}) =>
			updateReceptionParticipantActivities(
				constructionSiteId,
				participantId,
				data,
			),
		onSuccess: () => {
			queryClient.invalidateQueries({
				queryKey:
					constructionSiteSubmissionQueryKeys.participantsReception(
						constructionSiteId,
					),
			});
		},
	});
};

export const deleteFileFromConstructionSiteReception = async (
	constructionSiteId: number,
	fileId: string,
) => {
	await privateAxios.delete(
		`/construction-sites-reception/${constructionSiteId}/documents/${fileId}`,
	);
};

export const useDeleteFileFromConstructionSiteReception = (
	constructionSiteId: number,
) => {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: (fileId: string) =>
			deleteFileFromConstructionSiteReception(constructionSiteId, fileId),
		onSuccess: () => {
			queryClient.invalidateQueries({
				queryKey: [
					...constructionSiteSubmissionQueryKeys.detail(constructionSiteId),
					"reception",
				],
			});
			queryClient.invalidateQueries({
				queryKey:
					constructionSiteSubmissionQueryKeys.participantsReception(
						constructionSiteId,
					),
			});
			queryClient.invalidateQueries({
				queryKey: [
					...constructionSiteAnalysisQueryKeys.analysisData(constructionSiteId),
					"reception-supporting-documents-analysis",
				],
			});
			queryClient.invalidateQueries({
				queryKey: [
					...constructionSiteReceptionQueryKeys.detail(constructionSiteId),
					"operation-files",
				],
			});
			toast.success("Fichier supprimé avec succès");
		},
		onError: (error) => {
			const errorMessage = getErrorMessage(error);
			toast.error("Erreur lors de la suppression du fichier", {
				description: errorMessage,
			});
		},
	});
};
